import { useState } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { usePostComposer } from '~publish/hooks/usePostComposer'
import usePublishUploader from '~publish/legacy/uploads/hooks/usePublishUploader'
import { logError } from '~publish/legacy/utils/logError'

import { uploadInsightsImage } from './uploadInsightsImage'
import { captureChartAsBlob } from '../chartToImage'
import type {
  FollowersBreakdownSnapshot,
  RankedBarsSnapshot,
} from '../components/ChartSnapshot'
import type { MetricSeries } from '../components/MetricChart/types'

interface UseExportChartAsPostOptions {
  channelIds?: string[]
  text?: string
  heading?: string
  subheading?: string
  /**
   * Alt text written to the prefilled media for accessibility.
   * Defaults to the heading + subheading when omitted.
   */
  altText?: string
  /** Channel display name — drives the avatar fallback initial. */
  channelName?: string
  /** Raw service id (e.g. "instagram") — drives the snapshot's icon overlay. */
  channelService?: string
  /** Channel avatar URL — routed through Buffer's image proxy by the capture function. */
  channelAvatarUrl?: string
  /**
   * Followers breakdown view (Bar / Growth) to snapshot instead of the
   * time-series chart. When set, the snapshot renders the matching breakdown
   * chart so the shared image matches the on-screen view. @see ChartSnapshot
   */
  followersBreakdown?: FollowersBreakdownSnapshot
  /**
   * Ranked horizontal bars (e.g. the Posts "Bar" view) to snapshot instead of
   * the time-series chart. When set, the snapshot renders them so the shared
   * image matches the on-screen view. @see ChartSnapshot
   */
  rankedBars?: RankedBarsSnapshot
  /**
   * Chart data — re-rendered fresh inside the snapshot card so the captured
   * chart is sized for social aspect ratios instead of the live UI's wider
   * dimensions.
   */
  chartMetrics: MetricSeries[]
  chartStartDate: Date
  chartEndDate: Date
  /** Render the time-series data as stacked bars rather than a line. */
  chartStacked?: boolean
  /** Render as a composed bar + line chart (the Content Impact view). @see ChartSnapshot */
  chartComposed?: boolean
  /** Force a single shared Y-axis (e.g. All-Channels, where every line is the
   * same metric/unit). @see TimeSeriesChart */
  chartSingleAxis?: boolean
  /** Month-only date labels (no day) for monthly-bucketed data. @see TimeSeriesChart */
  chartMonthly?: boolean
  /** Y-axis label (metric name) for charts whose series titles are channel
   * names rather than the metric, e.g. All-Channels. @see TimeSeriesChart */
  chartYAxisLabel?: string
}

interface UseExportChartAsPostResult {
  onExport: () => Promise<void>
  isExporting: boolean
}

export function useExportChartAsPost({
  channelIds,
  text = 'Check out how my channels are performing! 📊',
  heading,
  subheading,
  altText,
  channelName,
  channelService,
  channelAvatarUrl,
  followersBreakdown,
  rankedBars,
  chartMetrics,
  chartStartDate,
  chartEndDate,
  chartStacked,
  chartComposed,
  chartSingleAxis,
  chartMonthly,
  chartYAxisLabel,
}: UseExportChartAsPostOptions): UseExportChartAsPostResult {
  const [isExporting, setIsExporting] = useState(false)

  const { createNewPostInComposer } = usePostComposer()

  const uploader = usePublishUploader({ id: 'insights-chart-export' })

  const onExport = async (): Promise<void> => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const { blob, width, height } = await captureChartAsBlob({
        channelName,
        channelService,
        channelAvatarUrl,
        heading: heading ?? 'Buffer Insights',
        subheading,
        followersBreakdown,
        rankedBars,
        chartMetrics,
        chartStartDate,
        chartEndDate,
        chartStacked,
        chartComposed,
        chartSingleAxis,
        chartMonthly,
        chartYAxisLabel,
      })

      const file = new File([blob], 'insights-chart.png', { type: 'image/png' })
      const url = await uploadInsightsImage({
        uploader,
        file,
        sourceName: 'insights-chart',
      })

      // The join always returns a string (possibly empty), so the trailing
      // `?? 'Buffer Insights chart'` on the original chained expression was
      // dead code. Compute the joined string first, then fall back on the
      // empty case so we always emit a non-empty alt-text.
      const joinedHeadings = [heading, subheading].filter(Boolean).join(' — ')
      const resolvedAltText =
        altText ?? (joinedHeadings || 'Buffer Insights chart')

      void createNewPostInComposer({
        cta: 'publish-insights-chart-exportToPost-1',
        channels: channelIds ?? 'all',
        prefillPostData: {
          text,
          media: {
            picture: url,
            photo: url,
            thumbnail: url,
            width: String(Math.round(width)),
            height: String(Math.round(height)),
            progress: '100',
            uploaded: 'true',
            alt_text: resolvedAltText,
          },
        },
      })
    } catch (error) {
      logError(error, { context: 'exportInsightsChartAsPost' })
      toast.error('Failed to export chart. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return { onExport, isExporting }
}
